/**
 * AISummaryCard
 *
 * Displays Granola-style enhanced notes where the operator's original words
 * appear in black and AI-added context appears in muted gray. Falls back
 * to the plain AI summary when enhanced notes aren't available.
 */
import DOMPurify from 'dompurify';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import type { EnhancedNoteSegment } from './types';

interface AISummaryCardProps {
  summary: string;
  originalNotes?: string;
  enhancedNotes?: EnhancedNoteSegment[] | string | null;
}

export const AISummaryCard = ({
  summary,
  originalNotes,
  enhancedNotes,
}: AISummaryCardProps) => {
  const [showOriginal, setShowOriginal] = useState(false);

  const enhancedSegments = Array.isArray(enhancedNotes) ? enhancedNotes : null;
  const enhancedMarkup =
    typeof enhancedNotes === 'string' && enhancedNotes.trim().length > 0 ? enhancedNotes : null;
  const hasEnhancedNotes =
    (enhancedSegments?.length ?? 0) > 0 || Boolean(enhancedMarkup);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <Sparkles className="size-4 text-primary" />
        <CardTitle className="text-sm">
          {hasEnhancedNotes ? 'Enhanced Notes' : 'AI Summary'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasEnhancedNotes ? (
          <>
            {enhancedSegments ? (
              <>
                {/* Legend */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2 rounded-sm bg-foreground" />
                    <span className="font-medium">Your notes</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2 rounded-sm bg-muted-foreground/50" />
                    <span className="font-medium">AI enhanced</span>
                  </span>
                </div>

                {/* Enhanced notes with visual distinction — each segment on its own line */}
                <div className="space-y-1 text-sm leading-relaxed">
                  {enhancedSegments.map((segment, idx) => (
                    <p
                      key={idx}
                      className={
                        segment.source === 'original'
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      }
                    >
                      {segment.text}
                    </p>
                  ))}
                </div>
              </>
            ) : (
              <div
                className="prose prose-sm max-w-none text-sm leading-relaxed dark:prose-invert"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(enhancedMarkup ?? ''),
                }}
              />
            )}

            {/* Collapsible: original notes */}
            {originalNotes !== undefined && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto gap-1.5 px-0 py-1 text-xs text-muted-foreground"
                  onClick={() => setShowOriginal((prev) => !prev)}
                >
                  {showOriginal ? (
                    <ChevronUp className="size-3" />
                  ) : (
                    <ChevronDown className="size-3" />
                  )}
                  {showOriginal ? 'Hide original notes' : 'View original notes'}
                </Button>

                {showOriginal && (
                  <div className="mt-2 rounded-md border bg-muted/50 p-3">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {originalNotes || 'No notes recorded.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Fallback: plain AI summary */}
            <p className="text-sm font-medium leading-relaxed text-foreground">{summary}</p>

            {originalNotes !== undefined && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto gap-1.5 px-0 py-1 text-xs text-muted-foreground"
                  onClick={() => setShowOriginal((prev) => !prev)}
                >
                  {showOriginal ? (
                    <ChevronUp className="size-3" />
                  ) : (
                    <ChevronDown className="size-3" />
                  )}
                  {showOriginal ? 'Hide original notes' : 'View original notes'}
                </Button>

                {showOriginal && (
                  <div className="mt-2 rounded-md border bg-muted/50 p-3">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {originalNotes || 'No notes recorded.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
